import { Box, Button, Sheet } from '@hover/blueprint';
import { iPlus } from '@hover/icons';
import React from 'react';

import { imageTracking } from 'src/modules/homeownerApp/components/Customize/tracking';
import {
  imageIsAddedToProject,
  sortProjects,
} from 'src/modules/homeownerApp/components/Utils';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useSaveToProjectContext } from 'src/modules/homeownerApp/providers/SaveToProjectProvider';
import type { Project } from 'src/shared/graphql/generated';
import { useGetProjectsQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { buttonPressed } from 'src/shared/telemetry';

import { NewProjectCTA } from './NewProjectCTA';
import { ProjectMenuLoadingItem } from './ProjectLoadingItem';
import { ProjectMenuItem } from './ProjectMenuItem';

const AddToProject = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();
  const { displayImageToSave, generatedImageToSave } =
    useSaveToProjectContext();
  const { data, loading } = useGetProjectsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  const projects = sortProjects((data?.projects || []) as Project[]);

  // Favor display image if it exists, otherwise use the generated image
  const imageToFavorite =
    displayImageToSave || generatedImageToSave || undefined;

  const projectsExist = projects.length > 0;

  const handleNewProjectClick = () => {
    buttonPressed({
      ...trackingData,
      ...imageTracking(imageToFavorite),
      button_location: 'Save to module',
      button_text: 'New project',
      partner_id: partnerId,
      primary_cta: true,
      track_id: 'new-project',
    });
    setCurrentMenu(HomeownerAppMenuNames.NEW_PROJECT);
  };

  const imageToShow =
    generatedImageToSave?.generatedImagePath || generatedImageToSave?.imagePath;

  return (
    <Sheet.Contents
      contentKey="add-to-project"
      header={t('customize.addToProject.header')}
      maxContentHeight={{
        base: `calc(100vh - ${
          projectsExist ? '420px' : 'var(--motion-overflow)'
        })`,
        tablet: 'calc(100vh - 220px)',
      }}
      paddingBottom={{ base: projectsExist ? '85px' : '0px', tablet: 0 }}
      sx={{
        header: {
          paddingLeft: { base: 200, tablet: 400 },
        },
      }}
    >
      {loading ? (
        <ProjectMenuLoadingItem />
      ) : (
        <>
          {projects.map(project => (
            <ProjectMenuItem
              isChecked={imageIsAddedToProject(
                project,
                generatedImageToSave?.id,
              )}
              key={project.id}
              project={project}
            />
          ))}
        </>
      )}
      {!projectsExist && !loading && <NewProjectCTA image={imageToShow!} />}
      {projectsExist && (
        <Box
          backgroundColor="neutral.0"
          bottom={{ base: 'var(--motion-overflow)', tablet: 0 }}
          left={0}
          paddingX={800}
          paddingY={500}
          position="absolute"
          right={{ base: 0, tablet: 'var(--motion-overflow)' }}
          shadow="distance200"
        >
          <Button
            iconAfter={iPlus}
            onClick={handleNewProjectClick}
            size="large"
            width="100%"
          >
            {t('customize.addToProject.newProject.newProject')}
          </Button>
        </Box>
      )}
    </Sheet.Contents>
  );
};

export { AddToProject };
